import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  Form,
  Modal,
  Select,
  Upload,
  Steps,
  message,
  Row,
  Col,
  Button,
  DatePicker,
  Typography,
  Flex,
} from 'antd';
import { HourglassOutlined, InboxOutlined } from '@ant-design/icons';

import DistributionType, {
  distributionTypeAsString,
} from '../../../domain/enums/@shared/DistributionType';
import ContactsImportFormContext from '../../contexts/forms/contacts/ContactsImportFormContext';
import { FormCard, FormContainer } from '@leedpulse-monorepo/components';
import { getSenderCountryFlag } from '@leedpulse-monorepo/utils';
import { ContextStatus, channelAsString } from '@leedpulse-monorepo/enums';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import ContactsContext from '../../contexts/data/ContactsContext';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import NumbersContext from '../../contexts/data/NumbersContext';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Scenario from '../../../domain/models/Scenario';

const { Option } = Select;
const { Dragger } = Upload;
const { confirm, info } = Modal;

interface ContactsImportFormProps {
  subscriptionId: string;
  globalScenarios?: Scenario[];
}

const ContactsImportForm: React.FC<ContactsImportFormProps> = ({
  subscriptionId,
  globalScenarios = [],
}) => {
  const navigate = useNavigate();
  const formContext = useContext(ContactsImportFormContext);
  const contactsContext = useContext(ContactsContext);
  const numbersContext = useContext(NumbersContext);
  const [currentStepUploadFile, setCurrentStepUploadFile] = useState(0);
  const [mappingStepDisabled, setMappingStepDisabled] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    formContext.initWithSubscriptionId(subscriptionId, globalScenarios);
  }, []);

  useEffect(() => {
    if (formContext.subscription) {
      numbersContext.fetchFromSenders(formContext.subscription.sender);
    }
  }, [formContext.subscription]);

  useEffect(() => {
    if (formContext.file) {
      formContext.retrieveFileHeaders();
    }
  }, [formContext.file]);

  useEffect(() => {
    if (formContext.contactScheduled) {
      formContext.onContactScheduledDateChange(dayjs());
    } else {
      formContext.onContactScheduledDateChange(null);
    }
  }, [formContext.contactScheduled]);

  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [lastMapping, setLastMapping] = useState(formContext.uploadedFileMapping);
  if (formContext.uploadedFileMapping !== lastMapping) {
    setLastMapping(formContext.uploadedFileMapping);
    if (formContext.uploadedFileMapping.length > 0) {
      let disabled = false;
      formContext.uploadedFileMapping.forEach((field) => {
        if (field.required && field.mapping.length === 0) {
          disabled = true;
        }
      });
      setMappingStepDisabled(disabled);
    }
  }

  useEffect(() => {
    if (formContext.statusHeaders === ContextStatus.SUCCESS) {
      if (formContext.file && formContext.uploadedFileHeaders.length > 0) {
        // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
        // chargement declenche ici. Architecture Context + use case du repo.
        // eslint-disable-next-line react/set-state-in-effect
        setCurrentStepUploadFile(1);
      } else {
        setCurrentStepUploadFile(0);
        message.error(
          "Une erreur s'est produite lors de la récupération des entêtes du fichier. Veuillez vérifier le fichier et réessayer."
        );
      }
    }
  }, [formContext.statusHeaders]);

  useEffect(() => {
    switch (formContext.status) {
      case ContextStatus.SUCCESS:
        Modal.info({
          title: 'Import terminé',
          centered: true,
          content: (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  formContext.importMessage ||
                  'Importation des contacts terminée avec succès',
              }}
            />
          ),
          onOk: () => {
            setHasSubmitted(false);
            contactsContext.refresh();
            formContext.reset();
            navigate(
              SUBSCRIPTIONS_PAGE_PATH +
                '/' +
                subscriptionId +
                CONTACTS_PAGE_PATH
            );
          },
        });
        break;
      case ContextStatus.ERROR:
        Modal.error({
          title: 'Import terminé',
          centered: true,
          content: (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  formContext.importMessage ||
                  'Importation des contacts terminée avec des erreurs',
              }}
            />
          ),
          onOk: () => {
            setHasSubmitted(false);
            contactsContext.refresh();
            formContext.reset();
            navigate(
              SUBSCRIPTIONS_PAGE_PATH +
                '/' +
                subscriptionId +
                CONTACTS_PAGE_PATH
            );
          },
        });
        break;
      default:
        break;
    }
  }, [formContext.status]);

  const handleFileChange = (info: UploadChangeParam<UploadFile<any>>) => {
    if (info.file.status === 'removed') {
      formContext.onFileRemove();
    }
  };

  const beforeUpload = (file: File) => {
    formContext.onFileChange(file);
    return false;
  };

  const isFormValid = () => {
    if (formContext.contactScheduled) {
      if (!formContext.distribution || !formContext.contactScheduledDate) {
        return false;
      }
    }

    if (
      !formContext.owner ||
      !formContext.sender ||
      !formContext.prospectCategory
    ) {
      return false;
    }

    return true;
  };

  const isBusy = formContext.status === ContextStatus.LOADING;

  const renderFieldSelects = (
    field: (typeof formContext.uploadedFileMapping)[0]
  ) => {
    const upto = field.upto || 1;
    const colsPerRow = upto > 3 ? 3 : upto;
    const items = [...Array(upto)];

    // Divide into groups of colsPerRow
    const rows = [];
    for (let i = 0; i < items.length; i += colsPerRow) {
      rows.push(items.slice(i, i + colsPerRow));
    }

    return rows.map((rowItems, rowIndex) => {
      const span = 24 / rowItems.length;

      return (
        <Row gutter={[16, 4]} key={`row-${field.key}-${rowIndex}`}>
          {rowItems.map((_, colIndex) => {
            const index = rowIndex * colsPerRow + colIndex;
            return (
              <Col span={span} key={field.key + '-' + index}>
                <Select
                  showSearch
                  style={{ width: '100%', marginBottom: '8px' }}
                  key={'select-' + index}
                  defaultValue={field.mapping[index] || undefined}
                  onChange={(value, obj) => {
                    let newFileMapping = [...formContext.uploadedFileMapping];
                    newFileMapping = newFileMapping.map((f) => {
                      if (f.key === field.key) {
                        f.mapping[index] = value;
                      }
                      return f;
                    });
                    formContext.onFileMappingChange(newFileMapping);
                  }}
                >
                  {formContext.uploadedFileHeaders.map(
                    (header, headerIndex) => (
                      <Select.Option key={headerIndex} value={header}>
                        {header}
                      </Select.Option>
                    )
                  )}
                </Select>
              </Col>
            );
          })}
        </Row>
      );
    });
  };

  return formContext.subscription?.id !== subscriptionId ? (
    <Card loading />
  ) : (
    <>
      <Modal
        title={<Flex align="center"><HourglassOutlined style={{ marginRight: 6 }} /> Import en cours</Flex>}
        centered
        open={formContext.status === ContextStatus.LOADING}
        footer={null}
        closable={false}
        maskClosable={true}
        destroyOnClose
      >
        <p>
          Veuillez patienter pendant que nous importons vos contacts. Cette
          opération peut prendre quelques minutes.
        </p>
      </Modal>
      <FormCard
        title="Importer des contacts"
        showTitle={false}
        onCreated={() => {
          if (
            !formContext.file ||
            mappingStepDisabled ||
            currentStepUploadFile !== 2
          ) {
            let textProblem = '';
            if (!formContext.file) {
              textProblem = 'Veuillez commencer par charger un fichier';
            } else if (mappingStepDisabled) {
              textProblem = "Le mapping des champs n'est pas terminé";
            } else if (currentStepUploadFile !== 2) {
              textProblem =
                "Vous n'avez pas terminé la configuration des options";
            }
            info({
              centered: true,
              okText: 'Ok',
              title: 'Oups !',
              content: textProblem,
              async onOk() { return Promise.resolve(); },
            });
          } else {
            confirm({
              centered: true,
              cancelText: 'Annuler',
              okText: 'Continuer',
              okType: 'primary',
              title: 'Êtes-vous sûr ?',
              content: formContext.contactScheduled
                ? 'Vous êtes sur le point d\'importer un fichier avec une répartition sur "' +
                  formContext.distribution +
                  '". Êtes-vous sûr de vouloir continuer ?'
                : "Vous êtes sur le point d'importer un fichier sans programmer une prise de contact. Êtes-vous sûr de vouloir continuer ?",
              async onOk() {
                const isValid = isFormValid();
                if (!isDirty) {
                  setIsDirty(true);
                }

                if (isValid) {
                  formContext.submit();
                  if (!hasSubmitted) setHasSubmitted(true);
                }
              },
              onCancel() { return; },
            });
          }
        }}
        isCreateLoading={formContext.status === ContextStatus.LOADING}
      >
        {/* Stepper header */}
        <Steps
          progressDot
          current={currentStepUploadFile}
          items={[
            {
              title: 'Fichier',
            },
            {
              title: 'Mapping',
            },
            {
              title: 'Options',
            },
          ]}
        />

        <Form layout="vertical">
          <div style={{ marginTop: '50px' }}>
            {currentStepUploadFile === 0 && (
              <Form.Item label="Fichier" required name="file">
                <Dragger
                  maxCount={1}
                  disabled={formContext.status === ContextStatus.LOADING}
                  beforeUpload={beforeUpload}
                  onChange={handleFileChange}
                  showUploadList={true}
                  multiple={false}
                  accept=".xls,.xlsx,.numbers,.csv,.xsv,.json,.jsonl"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Cliquez ou faites glisser le fichier dans cette zone pour le
                    télécharger
                  </p>
                  <p className="ant-upload-hint">
                    Formats acceptés : .xls, .xlsx, .numbers, .csv, .xsv, .json
                    et .jsonl
                  </p>
                </Dragger>
              </Form.Item>
            )}

            {currentStepUploadFile === 1 && (
              <Form
                key={'stepper-file-import-mapping'}
                layout="vertical"
                name="basic"
              >
                {formContext.uploadedFileMapping.map((field) => (
                  <Row
                    key={'row-field-' + field.key}
                    gutter={[16, 4]}
                    className="hp-mt-16"
                  >
                    <Col md={8} span={24} key={field.key + '-title'}>
                      <span>{field.title}</span>
                      {field.required && (
                        <span style={{ fontSize: '9px', color: 'red' }}>
                          <i> (obligatoire)</i>
                        </span>
                      )}
                    </Col>
                    <Col md={16} span={24} key={field.key + '-fiels'}>
                      {renderFieldSelects(field)}
                    </Col>
                  </Row>
                ))}
                <Row justify="end">
                  <Col>
                    <Button
                      type="primary"
                      disabled={mappingStepDisabled}
                      loading={false}
                      onClick={() =>
                        setCurrentStepUploadFile(currentStepUploadFile + 1)
                      }
                    >
                      J'ai terminé le mapping des champs
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}

            {currentStepUploadFile === 2 && (
              <FormContainer>
                <Form.Item label="Conseiller">
                  <Select
                    disabled={isBusy}
                    onChange={(value) => {
                      formContext.onOwnerChange(value as string);
                    }}
                    value={formContext.owner}
                  >
                    {formContext.subscriptionOwners.map((owner) => (
                      <Select.Option key={owner.uid} value={owner.uid}>
                        {owner.firstname + ' ' + owner.lastname}
                      </Select.Option>
                    ))}
                  </Select>
                  {isDirty && !formContext.owner && (
                    <Typography.Text type="danger">
                      Le conseiller est obligatoire
                    </Typography.Text>
                  )}
                </Form.Item>

                <Form.Item label="N° d'envoyeur">
                  <Select
                    disabled={isBusy}
                    loading={
                      numbersContext.sendersStatus === ContextStatus.LOADING
                    }
                    onChange={(value) => {
                      // Remove spaces
                      value = value.replace(/\s/g, '');
                      formContext.onSenderChange(value as string);
                    }}
                    value={formContext.sender}
                  >
                    {numbersContext.items.map((number) => (
                      <Select.Option key={number.sender} value={number.sender}>
                        {`${getSenderCountryFlag(number.sender)} ${
                          number.sender
                        } (${channelAsString(number.channel)}) - ${
                          number.provider
                        }`}
                      </Select.Option>
                    ))}
                  </Select>
                  {isDirty && !formContext.sender && (
                    <Typography.Text type="danger">
                      Le n° d'envoyeur est obligatoire
                    </Typography.Text>
                  )}
                </Form.Item>

                <Form.Item label="Scénario">
                  <Select
                    onChange={(value) =>
                      formContext.onProspectCategoryChange(value as string)
                    }
                    value={formContext.prospectCategory}
                  >
                    {formContext.subscriptionScenarios.map((scenario) => (
                      <Select.Option key={scenario.id} value={scenario.id}>
                        {scenario.name}
                      </Select.Option>
                    ))}
                  </Select>
                  {isDirty && !formContext.prospectCategory && (
                    <Typography.Text type="danger">
                      Le scénario est obligatoire
                    </Typography.Text>
                  )}
                </Form.Item>

                <Form.Item label="Programmer la prise de contact">
                  <Select
                    value={formContext.contactScheduled}
                    onChange={(value) => {
                      formContext.onContactScheduledChange(value);
                    }}
                  >
                    <Select.Option value={true}>Oui</Select.Option>
                    <Select.Option value={false}>Non</Select.Option>
                  </Select>
                </Form.Item>

                {formContext.contactScheduled && (
                  <Form.Item label="Date de début de prise de contact">
                    <DatePicker
                      style={{ width: '100%' }}
                      format={'DD/MM/YYYY'}
                      defaultValue={dayjs()}
                      onChange={(date) =>
                        formContext.onContactScheduledDateChange(date)
                      }
                    />
                    {isDirty && !formContext.contactScheduledDate && (
                      <Typography.Text type="danger">
                        La date de début de prise de contact est obligatoire
                      </Typography.Text>
                    )}
                  </Form.Item>
                )}

                {formContext.contactScheduled && (
                  <Form.Item label="Durée de la répartition">
                    <Select
                      value={formContext.distribution}
                      onChange={(value) =>
                        formContext.onDistributionChange(value)
                      }
                    >
                      {Object.keys(DistributionType).map((key) => (
                        <Option
                          key={key}
                          value={
                            DistributionType[
                              key as keyof typeof DistributionType
                            ]
                          }
                        >
                          {distributionTypeAsString(
                            DistributionType[
                              key as keyof typeof DistributionType
                            ]
                          )}
                        </Option>
                      ))}
                    </Select>
                    {isDirty && !formContext.distribution && (
                      <Typography.Text type="danger">
                        La durée de la répartition est obligatoire
                      </Typography.Text>
                    )}
                  </Form.Item>
                )}
              </FormContainer>
            )}
          </div>
        </Form>
      </FormCard>
    </>
  );
};

export default ContactsImportForm;
